import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaTrash, FaSignOutAlt, FaTrophy } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";

const SportsControl = () => {
  const [sports, setSports] = useState([]);
  const [newSportName, setNewSportName] = useState("");
  const [loading, setLoading] = useState(true);
  const [addingLoading, setAddingLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Fetch sports on component mount
  useEffect(() => {
    fetchSports();
  }, []);

  const fetchSports = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/sports`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        // Sort sports by id in ascending order
        const sortedData = data.sort((a, b) => a.id - b.id);
        setSports(sortedData);
      } else {
        setMessage({ type: "error", text: "Failed to fetch sports" });
      }
    } catch (error) {
      console.error("Error fetching sports:", error);
      setMessage({ type: "error", text: "Error fetching sports" });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSport = async (e) => {
    e.preventDefault();
    if (!newSportName.trim()) {
      setMessage({ type: "error", text: "Sport name is required" });
      return;
    }

    setAddingLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/sports`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name: newSportName.trim() }),
          credentials: "include",
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSports([...sports, data.sport]);
        setNewSportName("");
        setMessage({ type: "success", text: "Sport added successfully!" });
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to add sport",
        });
      }
    } catch (error) {
      console.error("Error adding sport:", error);
      setMessage({ type: "error", text: "Error adding sport" });
    } finally {
      setAddingLoading(false);
    }
  };

  const handleDeleteSport = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sport?")) {
      return;
    }

    setDeletingId(id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/sports/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        setSports(sports.filter((sport) => sport.id !== id));
        setMessage({ type: "success", text: "Sport deleted successfully!" });
      } else {
        const data = await response.json();
        setMessage({
          type: "error",
          text: data.message || "Failed to delete sport",
        });
      }
    } catch (error) {
      console.error("Error deleting sport:", error);
      setMessage({ type: "error", text: "Error deleting sport" });
    } finally {
      setDeletingId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Clear message after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
            <div className="flex items-center space-x-3">
              <FaTrophy className="text-3xl text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-800">
                Sports Control Panel
              </h1>
            </div>
            <div className="flex items-center sm:space-x-3 gap-2 sm:gap-0 flex-wrap sm:flex-nowrap">
              <button
                onClick={() => navigate("/DsportAdmin")}
                className="flex items-center space-x-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 w-full sm:w-auto justify-center"
              >
                <span>Back</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 w-full sm:w-auto justify-center"
              >
                <FaSignOutAlt />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Message Display */}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === "success"
                ? "bg-green-100 border border-green-400 text-green-700"
                : "bg-red-100 border border-red-400 text-red-700"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Add Sport Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Add New Sport
          </h2>
          <form onSubmit={handleAddSport} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 w-full">
              <input
                type="text"
                value={newSportName}
                onChange={(e) => setNewSportName(e.target.value)}
                placeholder="Enter sport name..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                disabled={addingLoading}
              />
            </div>
            <div className="w-full md:w-auto">
              <button
                type="submit"
                disabled={addingLoading}
                className="flex items-center justify-center w-full md:w-auto space-x-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-6 py-2 rounded-lg transition-colors duration-200"
              >
                {addingLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <FaPlus />
                )}
                <span>{addingLoading ? "Adding..." : "Add Sport"}</span>
              </button>
            </div>
          </form>
        </div>

        {/* Sports List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Existing Sports ({sports.length})
          </h2>

          {sports.length === 0 ? (
            <div className="text-center py-8">
              <FaTrophy className="text-6xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No sports found</p>
              <p className="text-gray-400">
                Add your first sport using the form above
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sports.map((sport) => (
                <div
                  key={sport.id}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-800 truncate">
                        {sport.name}
                      </h3>
                      <p className="text-sm text-gray-500">ID: {sport.id}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteSport(sport.id)}
                      disabled={deletingId === sport.id}
                      className="ml-2 p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors duration-200 disabled:opacity-50"
                      title="Delete sport"
                    >
                      {deletingId === sport.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                      ) : (
                        <FaTrash />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SportsControl;
